import { useMutation } from '@apollo/client';
import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { faThumbsDown as faThumbsDownSolid, faThumbsUp as faThumbsUpSolid } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';

import { withIcon } from '../../core/components/Icon.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { graphqlErrorMessage } from '../errors.ts';
import { RATE_CONVERSATION } from '../queries.ts';

const ThumbsUpIcon = withIcon(faThumbsUp);
const ThumbsUpActiveIcon = withIcon(faThumbsUpSolid);
const ThumbsDownIcon = withIcon(faThumbsDown);
const ThumbsDownActiveIcon = withIcon(faThumbsDownSolid);

interface ConversationFeedbackProps {
    conversationId: string;
    userRatingHelpful: boolean | null;
    buttonClassName?: string;
}

export const ConversationFeedback = ({
    conversationId,
    userRatingHelpful,
    buttonClassName,
}: ConversationFeedbackProps) => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const [rateConversation, { loading }] = useMutation(RATE_CONVERSATION);

    const handleRate = async (helpful: boolean) => {
        try {
            await rateConversation({
                variables: { id: conversationId, helpful: helpful === userRatingHelpful ? null : helpful },
            });
        } catch (e) {
            publishToast({ description: graphqlErrorMessage(e, t('thread.feedback.error')), status: 'error' });
        }
    };

    const ratings = [
        {
            helpful: true,
            label: t('thread.feedback.helpful'),
            Icon: userRatingHelpful === true ? ThumbsUpActiveIcon : ThumbsUpIcon,
        },
        {
            helpful: false,
            label: t('thread.feedback.notHelpful'),
            Icon: userRatingHelpful === false ? ThumbsDownActiveIcon : ThumbsDownIcon,
        },
    ];

    return (
        <>
            {ratings.map(({ helpful, label, Icon }) => (
                <Tooltip key={label} content={label}>
                    <button
                        type="button"
                        aria-label={label}
                        aria-pressed={userRatingHelpful === helpful}
                        disabled={loading}
                        onClick={() => handleRate(helpful)}
                        className={classnames(
                            buttonClassName,
                            userRatingHelpful === helpful && 'text-content-brand-strong hover:text-content-brand-strong'
                        )}
                    >
                        <Icon fixedWidth />
                    </button>
                </Tooltip>
            ))}
        </>
    );
};
